/*
  collection-soldout-merge.js

  Sold-out push-down, browser side. Two jobs.

  1. MERGE. The server already splits each paginated page: in-stock cards render
     in #main-collection-product-grid, sold-out cards render in the sibling grid
     #main-collection-soldout. Infinite scroll appends every later batch to the
     end of the main grid, in-stock and sold-out mixed together, because the
     library harvests ".is_infinite .product-index" from the fetched page and
     appends it all to the container it was bound to. This script moves each
     appended batch's sold-out cards into the block, so one sold-out block stays
     at the true bottom of the page.

  2. EARLY TRIGGER. Added at Stop 5, after measurement. See the long note below.

  Notes for whoever reads this next:
  - It observes #CollectionProductGrid, NOT the main grid. A sort or filter
    change re-renders through the Section Rendering API and replaces
    #CollectionProductGrid.innerHTML, which destroys and rebuilds the whole
    subtree below it. An observer bound to the main grid would be left
    watching a detached node. #CollectionProductGrid itself survives.
  - Both containers are queried fresh on every run for the same reason, and so
    is the infinite scroll instance, which is also replaced on a sort change.
  - It never clones. Moving preserves quick view, lazy loading and AOS state
    on cards that have already rendered.
  - It does not touch assets/theme.js or assets/stylesheet.css.

  WHY THE EARLY TRIGGER EXISTS, because the reason is not obvious and the
  original scope argued the opposite:

    Metafizzy InfiniteScroll is bound to the MAIN GRID and measures its
    scrollThreshold from the bottom of that container. The sold-out block
    begins about 50 px after the main grid ends. So with the library default
    of 400 px, the batch loads at the exact moment the sold-out block is on
    screen, and the batch is then inserted directly ABOVE the block, shoving
    it down under the reader's eye.

    Measured on the staging baby collection, cumulative layout shift over a
    full scroll was 0.3017 against 0.0662 on live, and every large shift named
    #main-collection-soldout as its source. Blocking this script entirely
    still scored 0.2333, so most of the cost was structural rather than from
    the merge itself. Raising the threshold is what addresses that structural
    part: the batch now loads while the block is still well below the fold, so
    the insert happens off screen.

    This does NOT hide, collapse or defer any product. Every card is in the
    server HTML on its own paginated page either way. Only the moment of
    loading changes.

    It also cannot lean on native scroll anchoring, which the scope assumed
    was available. It is not: assets/stylesheet.css sets
    "body{overflow-anchor:none}", so the browser does no compensation for
    content inserted above the viewport anywhere on this store. That rule is
    pre-existing and deliberately left alone here.
*/

(function () {
  'use strict';

  var WRAPPER = 'CollectionProductGrid';
  var MAIN_GRID = 'main-collection-product-grid';
  var SOLDOUT_BLOCK = 'main-collection-soldout';
  var SOLDOUT_CARDS = '.product-index[data-availability="sold-out"]';
  var CARD = 'product-index';
  var COUNT = '[data-soldout-count]';

  var THRESHOLD_FLOOR = 1200;
  var RETRY_LIMIT = 10;
  var RETRY_MS = 300;
  var RESIZE_MS = 250;

  if (!window.MutationObserver) {
    return;
  }

  var wrapper = document.getElementById(WRAPPER);
  if (!wrapper) {
    return;
  }

  if (!document.getElementById(MAIN_GRID) && !document.getElementById(SOLDOUT_BLOCK)) {
    return;
  }

  // Guards against re-entering the move from a mutation raised by the move
  // itself. The queued follow-up run still happens; it finds nothing left to
  // move and exits, which is what makes reconcile idempotent.
  var moving = false;

  function countCards(block) {
    var n = 0;
    var child = block.firstElementChild;
    while (child) {
      if (child.classList.contains(CARD)) {
        n += 1;
      }
      child = child.nextElementSibling;
    }
    return n;
  }

  function reconcile() {
    if (moving) {
      return;
    }

    // Never cached: a sort re-render replaces both of these.
    var grid = document.getElementById(MAIN_GRID);
    var block = document.getElementById(SOLDOUT_BLOCK);
    if (!grid || !block) {
      return;
    }

    var stranded = grid.querySelectorAll(SOLDOUT_CARDS);
    if (!stranded.length) {
      return;
    }

    moving = true;
    try {
      // querySelectorAll returns document order, and appending in that order
      // preserves it. One fragment, one append, one layout pass.
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < stranded.length; i += 1) {
        fragment.appendChild(stranded[i]);
      }
      block.appendChild(fragment);

      block.removeAttribute('hidden');

      var counter = block.querySelector(COUNT);
      if (counter) {
        counter.textContent = String(countCards(block));
      }
    } finally {
      moving = false;
    }
  }

  // Two viewports of runway, never less than the floor. Read at call time so a
  // resize or an orientation change recomputes it rather than freezing the
  // value taken at load.
  function targetThreshold() {
    return Math.max(THRESHOLD_FLOOR, window.innerHeight * 2);
  }

  // Returns true once it has written to a live instance. The instance is looked
  // up fresh every time: a sort re-render builds a new grid element and
  // renderFilters() constructs a new InfiniteScroll against it, so a cached
  // reference would be stale exactly when it mattered.
  function applyThreshold() {
    if (!window.InfiniteScroll || typeof window.InfiniteScroll.data !== 'function') {
      return false;
    }
    var grid = document.getElementById(MAIN_GRID);
    if (!grid) {
      return false;
    }
    var instance = window.InfiniteScroll.data(grid);
    if (!instance || !instance.options) {
      return false;
    }
    instance.options.scrollThreshold = targetThreshold();
    return true;
  }

  // theme.js may not have constructed the instance yet when a deferred script
  // runs. Try now, then on load, then a bounded number of times, then stop.
  // Giving up is silent on purpose: without infinite scroll there is nothing
  // to bring forward, and the merge half still works.
  function startThreshold() {
    if (applyThreshold()) {
      return;
    }
    var onLoad = function () {
      if (applyThreshold()) {
        return;
      }
      var tries = 0;
      var tick = function () {
        if (applyThreshold()) {
          return;
        }
        tries += 1;
        if (tries >= RETRY_LIMIT) {
          return;
        }
        setTimeout(tick, RETRY_MS);
      };
      setTimeout(tick, RETRY_MS);
    };
    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }
  }

  // reconcile() itself is unchanged. The threshold is re-applied around it so a
  // sort re-render, which swaps in a fresh instance still on the library
  // default, gets the raised value back without reconcile needing to know.
  function onMutation() {
    reconcile();
    applyThreshold();
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(applyThreshold, RESIZE_MS);
  });

  new MutationObserver(onMutation).observe(wrapper, {
    childList: true,
    subtree: true
  });

  reconcile();
  startThreshold();
})();
